import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const TermsPage = () => {
  const lastUpdated = "March 6, 2026";

  return (
    <div className="min-h-screen bg-[#050505] pt-20">
      {/* Decorative orange orbs */}
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title="Terms of Service"
        description="Agentic Atoms terms of service. Read our terms and conditions for using our AI consulting services and website."
        path="/terms"
      />
      {/* Back button */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-[#FF6B00]/10 border border-[#FF6B00]/25 rounded-2xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#FF6B00]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Terms of Service</h1>
              <p className="text-sm text-[#71717A]">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-gray max-w-none"
        >
          <div className="space-y-8 text-[#A1A1AA] leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the Agentic Atoms website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Description of Services</h2>
              <p>
                Agentic Atoms provides AI consulting services, including but not limited to custom AI application development, workflow automation, e-commerce solutions, and business intelligence services. The specific scope of services will be defined in individual agreements with clients.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Use of Website</h2>
              <p className="mb-3">You agree to use our website only for lawful purposes and in a way that does not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Interfere with or disrupt the website or servers</li>
                <li>Attempt to gain unauthorized access to any part of the website</li>
                <li>Use automated systems to access the website without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and software, is the property of Agentic Atoms or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. User Communications</h2>
              <p>
                When you submit information through our contact forms, chatbot, or other communication channels, you grant us permission to use that information to respond to your inquiries and provide our services. You represent that any information you provide is accurate and complete.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. Disclaimer of Warranties</h2>
              <p>
                Our website and services are provided "as is" without any warranties, express or implied. We do not guarantee that the website will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, Agentic Atoms shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Agentic Atoms, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the website or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of service of these external sites. We encourage you to review their policies before engaging with them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">11. Governing Law</h2>
              <p>
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">12. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue to be valid and enforceable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">13. Contact Information</h2>
              <p>
                For any questions regarding these Terms of Service, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-white/[0.03] rounded-xl">
                <p><strong>Agentic Atoms</strong></p>
                <p>Email: connect@agenticatoms.com</p>
                <p>Phone: +1-227-241-6372</p>
                <p>Address: 5830 E 2nd St, Ste 7000 #30038, Casper, Wyoming 82609 US</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div></div>
    </div>
  );
};

export default TermsPage;
